import { c as _c } from "react/compiler-runtime";
import { createFieldErrorHandler, diToFormData, hookFormToVaultData, resolveBrowserDatePattern, toDateFormatPattern, VAULT_DOT_SEPARATOR, type VaultFormData, vaultDataToHookForm } from '@onefootprint/core';
import type { FieldError } from '@onefootprint/hooks';
import type { Language, Locale, OnboardingRequirementCollectData, PostHostedOnboardingPrefillChallengeResponse, PrefillContext, VaultData, VerifiedPrefillResponse } from '@onefootprint/request-types';
import { Button, Divider, PageShell, useConfirmationDialog } from '@onefootprint/ui';
import omitBy from 'lodash/omitBy';
import { useEffect, useState } from 'react';
import { type FieldErrors, FormProvider, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { PoweredByVerified } from '@/idv2/components/powered-by-verified';
import { getLogger, Logger } from '@/idv2/utils/logger';
import { BasicData } from './components/basic-data';
import { PoweredByVerifiedAndFootprint } from './components/powered-by-verified-and-footprint';
import { PrefillOverlay } from './components/prefill-overlay';
import { ResidentialAddress } from './components/residential-address';
import { ReviewInformation } from './components/review-information';
import { SsnOrTaxId } from './components/ssn-or-tax-id';
import { UsLegalStatusFields } from './components/us-legal-status-fields';
import { VerifiedPrefillOverlay } from './components/verified-prefill-overlay';
import { getDisabledDis, getSingleCountryDefaults, isCountryUsOrTerritories } from './utils/collect-data-helpers';
import { UsLegalStatus } from './utils/constants';
import { getCollectDataAttributes } from './utils/get-collect-data-dis';
import type { CitizenshipsFormData, UsLegalStatus as UsLegalStatusType } from './utils/types';
import { applyCitizenshipsToVaultData, getDefaultValuesFromVault } from './utils/us-legal-status';
type CombinedFormData = VaultFormData & CitizenshipsFormData;
export type CollectDataMode = 'review' | 'edit';
type CollectDataProps = {
  currentRequirement: OnboardingRequirementCollectData;
  isLoading: boolean;
  decryptedVault: VaultData;
  onSubmit: (data: VaultData, onError: (errors: FieldError[]) => void) => void;
  className?: string;
  l10n: {
    locale?: Locale;
    language: Language;
  };
  disableSsnValidation?: boolean;
  authToken: string;
  verifiedPrefillResponse?: VerifiedPrefillResponse;
  scrubbedSsn9Value?: string;
  scrubbedDobValue?: string;
  onPrefill: () => Promise<void>;
  footprintPrefillContext?: PrefillContext;
  footprintPrefillChallengeData?: PostHostedOnboardingPrefillChallengeResponse;
  /** Whether the compact review/confirm flow is active (the route decides; see canShowCompactReview). */
  canShowReview?: boolean;
  /** Current review/edit mode, owned by the route so it can drive the shell nav override. */
  mode?: CollectDataMode;
  onModeChange?: (mode: CollectDataMode) => void;
};
const {
  logError,
  logInfo,
  logWarn
} = getLogger({
  name: 'idv2',
  location: 'collect-data'
});

// Log DI names rather than RHF's `__DOT__` keys, so the output matches how Datadog is queried.
const describeFieldErrors = (formErrors: FieldErrors<CombinedFormData>): string => Object.entries(formErrors).map(([field, error]) => `${field.replaceAll(VAULT_DOT_SEPARATOR, '.')}:${error?.type ?? 'unknown'}`).join(',');
export const CollectData = t0 => {
  const $ = _c(157);
  const {
    l10n,
    currentRequirement,
    isLoading,
    decryptedVault,
    onSubmit,
    disableSsnValidation,
    authToken,
    verifiedPrefillResponse,
    scrubbedSsn9Value,
    scrubbedDobValue,
    onPrefill,
    footprintPrefillContext,
    footprintPrefillChallengeData,
    canShowReview: t1,
    mode: t2,
    onModeChange: t3
  } = t0;
  const canShowReview = t1 === undefined ? false : t1;
  const mode = t2 === undefined ? "edit" : t2;
  const onModeChange = t3 === undefined ? _temp : t3;
  let t4;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      keyPrefix: "collectData"
    };
    $[0] = t4;
  } else {
    t4 = $[0];
  }
  const {
    t
  } = useTranslation("idv2", t4);
  const confirmationDialog = useConfirmationDialog();
  const locale = l10n.locale;
  const attributes = getCollectDataAttributes(currentRequirement);
  const showsDob = attributes.includes("dob");
  let t5;
  if ($[1] !== currentRequirement || $[2] !== decryptedVault) {
    t5 = getDisabledDis(currentRequirement, decryptedVault);
    $[1] = currentRequirement;
    $[2] = decryptedVault;
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  const disabledDis = t5;
  const [isSsnSkipped, setIsSsnSkipped] = useState(false);
  const [editedData, setEditedData] = useState(null);
  let t6;
  if ($[4] !== currentRequirement.optionalAttributes) {
    t6 = currentRequirement.optionalAttributes.includes("ssn4") || currentRequirement.optionalAttributes.includes("ssn9") || currentRequirement.optionalAttributes.includes("us_tax_id");
    $[4] = currentRequirement.optionalAttributes;
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  const isSsnOptional = t6;
  const hasSsnAttributes = attributes.includes("ssn4") || attributes.includes("ssn9") || attributes.includes("us_tax_id");
  const showUsLegalStatus = attributes.includes("us_legal_status");
  let t7;
  let t8;
  if ($[6] !== locale || $[7] !== showsDob) {
    t7 = () => {
      if (!showsDob) {
        return;
      }
      Logger.appendGlobalContext({
        dob_date_format: toDateFormatPattern(locale) ?? resolveBrowserDatePattern(),
        dob_date_format_source: locale ? "sdk" : "browser"
      });
    };
    t8 = [showsDob, locale];
    $[6] = locale;
    $[7] = showsDob;
    $[8] = t7;
    $[9] = t8;
  } else {
    t7 = $[8];
    t8 = $[9];
  }
  useEffect(t7, t8);
  let t9;
  if ($[10] !== currentRequirement || $[11] !== decryptedVault) {
    t9 = vaultDataToHookForm({
      ...decryptedVault,
      ...getSingleCountryDefaults(currentRequirement, decryptedVault)
    });
    $[10] = currentRequirement;
    $[11] = decryptedVault;
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  let t10;
  if ($[13] !== decryptedVault || $[14] !== showUsLegalStatus) {
    t10 = showUsLegalStatus ? getDefaultValuesFromVault(decryptedVault) : {};
    $[13] = decryptedVault;
    $[14] = showUsLegalStatus;
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  let t11;
  if ($[16] !== t10 || $[17] !== t9) {
    t11 = {
      defaultValues: {
        ...t9,
        ...t10
      },
      mode: "onSubmit"
    };
    $[16] = t10;
    $[17] = t9;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  const methods = useForm(t11);
  const t12 = methods.control;
  let t13;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = diToFormData("id.country");
    $[19] = t13;
  } else {
    t13 = $[19];
  }
  let t14;
  if ($[20] !== methods.control) {
    t14 = {
      control: t12,
      name: t13
    };
    $[20] = methods.control;
    $[21] = t14;
  } else {
    t14 = $[21];
  }
  const watchedCountry = useWatch(t14);
  let t15;
  if ($[22] !== hasSsnAttributes || $[23] !== watchedCountry) {
    t15 = hasSsnAttributes && !!watchedCountry && isCountryUsOrTerritories(watchedCountry);
    $[22] = hasSsnAttributes;
    $[23] = watchedCountry;
    $[24] = t15;
  } else {
    t15 = $[24];
  }
  const showSsn = t15;
  let T0;
  let handleStatusChange;
  let hasVerifiedPrefillData;
  let t16;
  let t17;
  let t18;
  let title;
  if ($[25] !== attributes || $[26] !== canShowReview || $[27] !== confirmationDialog || $[28] !== currentRequirement.populatedAttributes || $[29] !== currentRequirement.residentialAddressCountries || $[30] !== decryptedVault || $[31] !== editedData || $[32] !== isLoading || $[33] !== isSsnSkipped || $[34] !== methods || $[35] !== mode || $[36] !== onModeChange || $[37] !== onSubmit || $[38] !== scrubbedDobValue || $[39] !== scrubbedSsn9Value || $[40] !== setEditedData || $[41] !== showUsLegalStatus || $[42] !== t || $[43] !== verifiedPrefillResponse?.result?.kind) {
    t18 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const handleFieldErrors = createFieldErrorHandler(methods);
      let t19;
      if ($[51] !== methods) {
        t19 = newStatus => {
          const currentNationality = methods.getValues(diToFormData("id.nationality"));
          const nextNationality = newStatus === UsLegalStatus.citizen ? "US" : currentNationality;
          methods.setValue(diToFormData("id.us_legal_status"), newStatus);
          methods.setValue(diToFormData("id.nationality"), nextNationality);
          methods.setValue("citizenships", [{
            value: undefined
          }]);
          methods.setValue(diToFormData("id.visa_kind"), undefined);
          methods.setValue(diToFormData("id.visa_expiration_date"), undefined);
          methods.clearErrors([diToFormData("id.nationality"), "citizenships", diToFormData("id.visa_kind"), diToFormData("id.visa_expiration_date")]);
        };
        $[51] = methods;
        $[52] = t19;
      } else {
        t19 = $[52];
      }
      handleStatusChange = t19;
      const submitData = data => {
        const vaultData = omitBy(hookFormToVaultData(data), _temp2) as VaultData;
        if (showUsLegalStatus) {
          applyCitizenshipsToVaultData(vaultData, data.citizenships);
        }
        for (const [cdo, di] of [["email", "id.email"], ["phone_number", "id.phone_number"]] as const) {
          if (currentRequirement.populatedAttributes.includes(cdo) && vaultData[di] === decryptedVault[di]) {
            delete vaultData[di];
          }
        }
        onSubmit(vaultData, handleFieldErrors);
      };
      const handleSubmit = async data_0 => {
        if (isSsnSkipped) {
          logInfo("SSN skip confirmation opened");
          confirmationDialog.open({
            title: t(_temp3),
            description: t(_temp4),
            primaryButton: {
              label: t(_temp5),
              onClick: () => submitData(data_0)
            },
            secondaryButton: {
              label: t(_temp6)
            }
          });
        } else {
          submitData(data_0);
        }
      };
      const handleValidSubmit = data_1 => {
        if (isLoading) {
          return;
        }
        if (canShowReview) {
          const vaultData_0 = omitBy(hookFormToVaultData(data_1), _temp7) as VaultData;
          setEditedData(vaultData_0);
          onModeChange("review");
          logInfo("Form edits captured, showing review");
          return;
        }
        handleSubmit(data_1);
      };
      const handleConfirm = () => {
        const values = methods.getValues();
        const cleaned = {
          ...values
        };
        const ssn9Key = diToFormData("id.ssn9");
        if (scrubbedSsn9Value && cleaned[ssn9Key] === scrubbedSsn9Value) {
          cleaned[ssn9Key] = "";
        }
        const dobKey = diToFormData("id.dob");
        if (scrubbedDobValue && cleaned[dobKey] === scrubbedDobValue) {
          cleaned[dobKey] = "";
        }
        submitData(cleaned);
      };
      hasVerifiedPrefillData = verifiedPrefillResponse?.result?.kind === "located";
      let t20;
      if ($[53] !== hasVerifiedPrefillData || $[54] !== t) {
        t20 = hasVerifiedPrefillData ? t(_temp8) : t(_temp9);
        $[53] = hasVerifiedPrefillData;
        $[54] = t;
        $[55] = t20;
      } else {
        t20 = $[55];
      }
      title = t20;
      let t21;
      if ($[56] !== editedData) {
        t21 = editedData ?? {};
        $[56] = editedData;
        $[57] = t21;
      } else {
        t21 = $[57];
      }
      let t22;
      if ($[58] !== decryptedVault || $[59] !== t21) {
        t22 = {
          ...decryptedVault,
          ...t21
        };
        $[58] = decryptedVault;
        $[59] = t21;
        $[60] = t22;
      } else {
        t22 = $[60];
      }
      const reviewData = t22;
      if (mode === "review") {
        let t23;
        if ($[61] !== t) {
          t23 = t(_temp10);
          $[61] = t;
          $[62] = t23;
        } else {
          t23 = $[62];
        }
        let t24;
        if ($[63] !== t) {
          t24 = t(_temp11);
          $[63] = t;
          $[64] = t24;
        } else {
          t24 = $[64];
        }
        let t25;
        if ($[65] !== attributes || $[66] !== reviewData) {
          t25 = <ReviewInformation data={reviewData} attributes={attributes} />;
          $[65] = attributes;
          $[66] = reviewData;
          $[67] = t25;
        } else {
          t25 = $[67];
        }
        let t26;
        if ($[68] !== hasVerifiedPrefillData) {
          t26 = hasVerifiedPrefillData && <PoweredByVerifiedAndFootprint />;
          $[68] = hasVerifiedPrefillData;
          $[69] = t26;
        } else {
          t26 = $[69];
        }
        let t27;
        if ($[70] !== t) {
          t27 = t(_temp12);
          $[70] = t;
          $[71] = t27;
        } else {
          t27 = $[71];
        }
        let t28;
        if ($[72] !== onModeChange) {
          t28 = () => onModeChange("edit");
          $[72] = onModeChange;
          $[73] = t28;
        } else {
          t28 = $[73];
        }
        let t29;
        if ($[74] !== t) {
          t29 = t(_temp13);
          $[74] = t;
          $[75] = t29;
        } else {
          t29 = $[75];
        }
        let t30;
        if ($[76] !== isLoading || $[77] !== t28 || $[78] !== t29) {
          t30 = <Button type="button" variant="secondary" fullWidth={true} size="large" disabled={isLoading} onClick={t28}>{t29}</Button>;
          $[76] = isLoading;
          $[77] = t28;
          $[78] = t29;
          $[79] = t30;
        } else {
          t30 = $[79];
        }
        const t31 = <div className="flex flex-col gap-3"><Button type="button" variant="primary" fullWidth={true} size="large" loading={isLoading} onClick={handleConfirm}>{t27}</Button>{t30}</div>;
        let t32;
        if ($[80] !== t25 || $[81] !== t26 || $[82] !== t31) {
          t32 = <div className="flex flex-col gap-6 w-full">{t25}{t26}{t31}</div>;
          $[80] = t25;
          $[81] = t26;
          $[82] = t31;
          $[83] = t32;
        } else {
          t32 = $[83];
        }
        let t33;
        if ($[84] !== t23 || $[85] !== t24 || $[86] !== t32) {
          t33 = <PageShell title={t23} subtitle={t24}>{t32}</PageShell>;
          $[84] = t23;
          $[85] = t24;
          $[86] = t32;
          $[87] = t33;
        } else {
          t33 = $[87];
        }
        let t34;
        if ($[88] !== methods || $[89] !== t33) {
          t34 = <FormProvider {...methods}>{t33}</FormProvider>;
          $[88] = methods;
          $[89] = t33;
          $[90] = t34;
        } else {
          t34 = $[90];
        }
        t18 = t34;
        break bb0;
      }
      T0 = FormProvider;
      t17 = methods;
      let t23;
      if ($[91] !== currentRequirement.residentialAddressCountries || $[92] !== methods) {
        t23 = formErrors => {
          logWarn("Form validation blocked submission", undefined, {
            fieldErrors: describeFieldErrors(formErrors),
            fieldErrorCount: Object.keys(formErrors).length
          });
          const countryError = formErrors[diToFormData("id.country")];
          if (countryError && currentRequirement.residentialAddressCountries?.length === 1) {
            logError("Country validation failed for single-country config", null, {
              residentialAddressCountries: currentRequirement.residentialAddressCountries?.join(","),
              countryFieldValue: methods.getValues(diToFormData("id.country"))
            });
          }
        };
        $[91] = currentRequirement.residentialAddressCountries;
        $[92] = methods;
        $[93] = t23;
      } else {
        t23 = $[93];
      }
      t16 = methods.handleSubmit(handleValidSubmit, t23);
    }
    $[25] = attributes;
    $[26] = canShowReview;
    $[27] = confirmationDialog;
    $[28] = currentRequirement.populatedAttributes;
    $[29] = currentRequirement.residentialAddressCountries;
    $[30] = decryptedVault;
    $[31] = editedData;
    $[32] = isLoading;
    $[33] = isSsnSkipped;
    $[34] = methods;
    $[35] = mode;
    $[36] = onModeChange;
    $[37] = onSubmit;
    $[38] = scrubbedDobValue;
    $[39] = scrubbedSsn9Value;
    $[40] = setEditedData;
    $[41] = showUsLegalStatus;
    $[42] = t;
    $[43] = verifiedPrefillResponse?.result?.kind;
    $[44] = T0;
    $[45] = handleStatusChange;
    $[46] = hasVerifiedPrefillData;
    $[47] = t16;
    $[48] = t17;
    $[49] = t18;
    $[50] = title;
  } else {
    T0 = $[44];
    handleStatusChange = $[45];
    hasVerifiedPrefillData = $[46];
    t16 = $[47];
    t17 = $[48];
    t18 = $[49];
    title = $[50];
  }
  if (t18 !== Symbol.for("react.early_return_sentinel")) {
    return t18;
  }
  let t19;
  if ($[94] !== t) {
    t19 = t(_temp14);
    $[94] = t;
    $[95] = t19;
  } else {
    t19 = $[95];
  }
  let t20;
  if ($[96] !== canShowReview || $[97] !== t) {
    t20 = canShowReview ? t(_temp15) : t(_temp16);
    $[96] = canShowReview;
    $[97] = t;
    $[98] = t20;
  } else {
    t20 = $[98];
  }
  let t21;
  if ($[99] !== t20) {
    t21 = {
      text: t20,
      type: "submit"
    };
    $[99] = t20;
    $[100] = t21;
  } else {
    t21 = $[100];
  }
  let t22;
  if ($[101] !== authToken || $[102] !== footprintPrefillChallengeData || $[103] !== footprintPrefillContext || $[104] !== onPrefill) {
    t22 = footprintPrefillContext && <PrefillOverlay authToken={authToken} onPrefill={onPrefill} context={footprintPrefillContext} challengeData={footprintPrefillChallengeData} />;
    $[101] = authToken;
    $[102] = footprintPrefillChallengeData;
    $[103] = footprintPrefillContext;
    $[104] = onPrefill;
    $[105] = t22;
  } else {
    t22 = $[105];
  }
  let t23;
  if ($[106] !== hasVerifiedPrefillData) {
    t23 = hasVerifiedPrefillData && <div className="bg-secondary rounded-md p-3 mb-8"><PoweredByVerified /></div>;
    $[106] = hasVerifiedPrefillData;
    $[107] = t23;
  } else {
    t23 = $[107];
  }
  let t24;
  if ($[108] !== attributes || $[109] !== disabledDis || $[110] !== locale || $[111] !== scrubbedDobValue) {
    t24 = <BasicData attributes={attributes} locale={locale} disabledDis={disabledDis} scrubbedDobValue={scrubbedDobValue} />;
    $[108] = attributes;
    $[109] = disabledDis;
    $[110] = locale;
    $[111] = scrubbedDobValue;
    $[112] = t24;
  } else {
    t24 = $[112];
  }
  let t25;
  if ($[113] !== attributes || $[114] !== currentRequirement.residentialAddressCountries || $[115] !== disabledDis || $[116] !== locale) {
    t25 = attributes.includes("full_address") && <><Divider /><ResidentialAddress locale={locale ?? "en-US"} residentialAddressCountries={currentRequirement.residentialAddressCountries} disabledDis={disabledDis} /></>;
    $[113] = attributes;
    $[114] = currentRequirement.residentialAddressCountries;
    $[115] = disabledDis;
    $[116] = locale;
    $[117] = t25;
  } else {
    t25 = $[117];
  }
  let t26;
  if ($[118] !== attributes || $[119] !== disableSsnValidation || $[120] !== disabledDis || $[121] !== isSsnOptional || $[122] !== isSsnSkipped || $[123] !== scrubbedSsn9Value || $[124] !== setIsSsnSkipped || $[125] !== showSsn) {
    t26 = showSsn && <><Divider /><SsnOrTaxId attributes={attributes} disabledDis={disabledDis} isOptional={isSsnOptional} isSkipped={isSsnSkipped} onSkipChange={() => setIsSsnSkipped(_temp17)} disableSsnValidation={disableSsnValidation} scrubbedSsn9Value={scrubbedSsn9Value} /></>;
    $[118] = attributes;
    $[119] = disableSsnValidation;
    $[120] = disabledDis;
    $[121] = isSsnOptional;
    $[122] = isSsnSkipped;
    $[123] = scrubbedSsn9Value;
    $[124] = setIsSsnSkipped;
    $[125] = showSsn;
    $[126] = t26;
  } else {
    t26 = $[126];
  }
  let t27;
  if ($[127] !== disabledDis || $[128] !== handleStatusChange || $[129] !== locale || $[130] !== showUsLegalStatus) {
    t27 = showUsLegalStatus && <><Divider /><UsLegalStatusFields locale={locale} onStatusChange={handleStatusChange} disabledDis={disabledDis} /></>;
    $[127] = disabledDis;
    $[128] = handleStatusChange;
    $[129] = locale;
    $[130] = showUsLegalStatus;
    $[131] = t27;
  } else {
    t27 = $[131];
  }
  let t28;
  if ($[132] !== t24 || $[133] !== t25 || $[134] !== t26 || $[135] !== t27) {
    t28 = <div className="flex flex-col gap-6 w-full">{t24}{t25}{t26}{t27}</div>;
    $[132] = t24;
    $[133] = t25;
    $[134] = t26;
    $[135] = t27;
    $[136] = t28;
  } else {
    t28 = $[136];
  }
  let t29;
  if ($[137] !== isLoading || $[138] !== t19 || $[139] !== t21 || $[140] !== t22 || $[141] !== t23 || $[142] !== t28 || $[143] !== title) {
    t29 = <PageShell title={title} subtitle={t19} cta={t21} isLoading={isLoading}>{t22}{t23}{t28}</PageShell>;
    $[137] = isLoading;
    $[138] = t19;
    $[139] = t21;
    $[140] = t22;
    $[141] = t23;
    $[142] = t28;
    $[143] = title;
    $[144] = t29;
  } else {
    t29 = $[144];
  }
  let t30;
  if ($[145] !== t16 || $[146] !== t29) {
    t30 = <form onSubmit={t16}>{t29}</form>;
    $[145] = t16;
    $[146] = t29;
    $[147] = t30;
  } else {
    t30 = $[147];
  }
  let t31;
  if ($[148] !== authToken || $[149] !== locale || $[150] !== verifiedPrefillResponse) {
    t31 = verifiedPrefillResponse?.result?.kind === "step_up" && <VerifiedPrefillOverlay locale={locale} authToken={authToken} stepUpKind={verifiedPrefillResponse.result.stepUpKind} />;
    $[148] = authToken;
    $[149] = locale;
    $[150] = verifiedPrefillResponse;
    $[151] = t31;
  } else {
    t31 = $[151];
  }
  let t32;
  if ($[152] !== T0 || $[153] !== t17 || $[154] !== t30 || $[155] !== t31) {
    t32 = <T0 {...t17}>{t30}{t31}</T0>;
    $[152] = T0;
    $[153] = t17;
    $[154] = t30;
    $[155] = t31;
    $[156] = t32;
  } else {
    t32 = $[156];
  }
  return t32;
};
function _temp() {}
function _temp2(value) {
  return value === "" || value === undefined;
}
function _temp3($) {
  return $.fields.ssn.skip.confirmation.title;
}
function _temp4($_0) {
  return $_0.fields.ssn.skip.confirmation.description;
}
function _temp5($_1) {
  return $_1.fields.ssn.skip.confirmation.yes;
}
function _temp6($_2) {
  return $_2.fields.ssn.skip.confirmation.no;
}
function _temp7(value_0) {
  return value_0 === "" || value_0 === undefined;
}
function _temp8($_3) {
  return $_3.pages.basicInformation.confirmTitle;
}
function _temp9($_4) {
  return $_4.pages.basicInformation.title;
}
function _temp10($_5) {
  return $_5.confirm.title;
}
function _temp11($_6) {
  return $_6.confirm.subtitle;
}
function _temp12($_7) {
  return $_7.confirm.confirm;
}
function _temp13($_8) {
  return $_8.confirm.edit;
}
function _temp14($_9) {
  return $_9.pages.basicInformation.subtitle;
}
function _temp15($_10) {
  return $_10.confirm.save;
}
function _temp16($_11) {
  return $_11.pages.basicInformation.cta;
}
function _temp17(prev) {
  return !prev;
}